import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import { useAuth } from "@/context/AuthContext";
import api, { money, openPdf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { Loader2, DollarSign, FileDown, AlertTriangle, Camera, Upload, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { UploadPhotosDialog, UploadDocDialog } from "./UploadDialogs";

export default function ContractorPayouts() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [dispute, setDispute] = useState(null);
  const [photoUp, setPhotoUp] = useState(null);
  const [docUp, setDocUp] = useState(null);
  const [rechecking, setRechecking] = useState(null);
  const load = () => api.get("/contractor/payouts").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const recheck = async (p) => {
    setRechecking(p.id);
    await load();
    setRechecking(null);
    toast.success("Eligibility updated.");
  };

  const totals = (rows || []).reduce((a, p) => ({
    pending: a.pending + (p.payment_status !== "Paid" ? p.balance : 0),
    paid: a.paid + (p.amount_paid || 0),
  }), { pending: 0, paid: 0 });

  return (
    <DashboardLayout nav={getNav(user.role)} title="My Payouts">
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-xs text-slate-400">Pending Balance</div><div className="font-head text-xl font-semibold text-amber-600" data-testid="cp-pending">{money(totals.pending)}</div></div>
        <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-xs text-slate-400">Total Paid</div><div className="font-head text-xl font-semibold text-green-600" data-testid="cp-paid">{money(totals.paid)}</div></div>
      </div>
      {!rows ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> :
        rows.length === 0 ? <div className="text-center py-16 bg-white rounded-xl border border-slate-200"><DollarSign className="w-10 h-10 text-slate-300 mx-auto" /><p className="mt-3 text-slate-500">No payouts yet.</p></div> : (
          <div className="space-y-2" data-testid="cp-list">
            {rows.map((p) => (
              <div key={p.id} data-testid={`cp-row-${p.id}`} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap"><span className="font-medium text-slate-900">{p.wo_number}</span><StatusBadge status={p.approval_status} /><StatusBadge status={p.payment_status} />{p.eligibility?.status && <span data-testid={`cp-elig-${p.id}`} className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${p.eligibility.status === "Eligible" ? "text-green-600 bg-green-50 border-green-200" : p.eligibility.status === "Not Eligible" ? "text-rose-600 bg-rose-50 border-rose-200" : p.eligibility.status === "Eligible with Warning" ? "text-amber-600 bg-amber-50 border-amber-200" : "text-slate-600 bg-slate-100 border-slate-300"}`}>{p.eligibility.status}</span>}</div>
                  <div className="text-sm text-slate-500 mt-0.5">{p.service_category} · {p.property?.name}, {p.property?.state}{p.completion_date ? ` · Completed ${p.completion_date}` : ""}</div>
                  {p.eligibility?.blocking_reason && <div data-testid={`cp-blocking-${p.id}`} className="text-xs text-rose-600 mt-1 flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {p.eligibility.blocking_reason}</div>}
                  {p.eligibility?.action_needed?.length > 0 && <div data-testid={`cp-action-${p.id}`} className="text-xs text-blue-600 mt-1">Action needed: {p.eligibility.action_needed.join(" · ")}</div>}
                  {p.payment_status !== "Unpaid" && <div className="text-xs text-slate-400 mt-0.5">Paid {money(p.amount_paid)} {p.last_method ? `· ${p.last_method}` : ""} {p.last_reference ? `· ${p.last_reference}` : ""} {p.last_payment_date ? `· ${p.last_payment_date}` : ""}</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className="font-head text-lg font-semibold text-slate-900">{money(p.payout)}</div>
                  <div className="flex gap-2 mt-1 justify-end">
                    <button data-testid={`cp-statement-${p.id}`} className="text-blue-600 text-xs underline flex items-center gap-1" onClick={() => openPdf(`/payouts/${p.id}/statement.pdf`)}><FileDown className="w-3 h-3" /> Statement</button>
                    {!p.disputed && p.payment_status !== "Paid" && <button data-testid={`cp-dispute-${p.id}`} className="text-rose-600 text-xs underline" onClick={() => setDispute(p)}>Dispute</button>}
                    {p.disputed && <span className="text-amber-600 text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Dispute open</span>}
                  </div>
                  {p.payment_status !== "Paid" && p.eligibility?.status && p.eligibility.status !== "Eligible" && (
                    <div className="flex gap-2 mt-2 justify-end flex-wrap" data-testid={`cp-fixit-${p.id}`}>
                      {p.eligibility.missing_after_photos && <Button size="sm" variant="outline" data-testid={`cp-upload-photo-${p.id}`} onClick={() => setPhotoUp(p)}><Camera className="w-3.5 h-3.5 mr-1" />Upload After Photos</Button>}
                      {p.eligibility.missing_documents && <Button size="sm" variant="outline" data-testid={`cp-upload-doc-${p.id}`} onClick={() => setDocUp(p)}><Upload className="w-3.5 h-3.5 mr-1" />Upload Document</Button>}
                      <Button size="sm" variant="outline" data-testid={`cp-viewwo-${p.id}`} onClick={() => window.open(`/app/wo/${p.work_order_id}`, "_blank")}><ExternalLink className="w-3.5 h-3.5 mr-1" />View Work Order</Button>
                      <Button size="sm" variant="ghost" data-testid={`cp-recheck-${p.id}`} disabled={rechecking === p.id} onClick={() => recheck(p)}>{rechecking === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RefreshCw className="w-3.5 h-3.5 mr-1" />Recheck</>}</Button>
                    </div>
                  )}

                </div>
              </div>
            ))}
          </div>
        )}
      {dispute && <DisputeDialog p={dispute} onClose={() => setDispute(null)} onDone={() => { setDispute(null); load(); }} />}
      {photoUp && <UploadPhotosDialog payout={photoUp} onClose={() => setPhotoUp(null)} onDone={() => { setPhotoUp(null); load(); }} />}
      {docUp && <UploadDocDialog payout={docUp} onClose={() => setDocUp(null)} onDone={() => { setDocUp(null); load(); }} />}
    </DashboardLayout>
  );
}

function DisputeDialog({ p, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState("");
  const [expl, setExpl] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) return toast.error("Reason is required");
    setBusy(true);
    try { await api.post(`/contractor/payouts/${p.id}/dispute`, { reason, requested_amount: amount ? Number(amount) : null, explanation: expl }); toast.success("Dispute submitted"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dispute-modal">
        <DialogHeader><DialogTitle>Dispute Payout — {p.wo_number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Reason</Label><Input data-testid="dispute-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5" /></div>
          <div><Label>Requested Amount (optional)</Label><Input data-testid="dispute-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5" /></div>
          <div><Label>Explanation</Label><Textarea data-testid="dispute-explanation" value={expl} onChange={(e) => setExpl(e.target.value)} rows={3} className="mt-1.5" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="dispute-submit" disabled={busy} onClick={submit} className="bg-rose-600 hover:bg-rose-700">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Dispute"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
